import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import './Module.css';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')
const ErrorCodes = require('../../../error-codes/error-codes.js')

const PilotFeedbackModule = ({user, reportId, isSmallScreen}) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [existingFeedback, setExistingFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
    const [isLoading, setIsLoading] = useState(true);

    // Get existing feedback when component mounts
    const getExistingFeedback = async () => {
        const url = URLS.GET_ONE_REPORT;
        const dict = {
            report_id: reportId,
            CLIENT_ACCESS_KEY: key
        };
        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                if (responseData && responseData[0] && responseData[0].pilot_feedback) {
                    setExistingFeedback(responseData[0].pilot_feedback);
                    setFeedbackText(responseData[0].pilot_feedback);
                }
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error getting existing feedback");
            } else {
                console.log("Unknown Error getting existing feedback");
            }
        });
        setIsLoading(false);
    };

    // Submit feedback to server
    const submitFeedback = async () => {
        if (!feedbackText.trim()) {
            setSubmitStatus('error');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        const url = URLS.UPDATE_PILOT_FEEDBACK;
        const dict = {
            report_id: reportId,
            pilot_feedback: feedbackText.trim(),
            CLIENT_ACCESS_KEY: key
        };
        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                setSubmitStatus('success');
                setExistingFeedback(feedbackText.trim());
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error submitting feedback");
                setSubmitStatus('error');
            } else {
                console.log("Unknown Error submitting feedback");
                setSubmitStatus('error');
            }
        });
        setIsSubmitting(false);
    };

    const handleTextChange = (e) => {
        setFeedbackText(e.target.value);
        // Clear status when user starts typing again
        if (submitStatus) {
            setSubmitStatus(null);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        submitFeedback();
    };

    useEffect(() => {
        getExistingFeedback();
    }, [reportId]);

    if (isLoading) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Pilot Feedback</h2>
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <h2 className='module-title'>Pilot Feedback</h2>
            
            <div style={{ marginBottom: '20px' }}>
                <p style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    marginBottom: '15px',
                    fontStyle: 'italic'
                }}>
                    Please include name and date in below feedback.
                </p>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '15px' }}>
                    <textarea
                        value={feedbackText}
                        onChange={handleTextChange}
                        placeholder="Enter your feedback here..."
                        style={{
                            width: '100%',
                            minHeight: '120px',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                        }}
                        disabled={isSubmitting}
                    />
                </div>

                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '15px'
                }}>
                    <button
                        type="submit"
                        disabled={isSubmitting || !feedbackText.trim()}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: isSubmitting || !feedbackText.trim() ? '#ccc' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isSubmitting || !feedbackText.trim() ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>

                    {submitStatus === 'success' && (
                        <span style={{ 
                            color: '#28a745', 
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}>
                            ✓ Feedback submitted successfully!
                        </span>
                    )}

                    {submitStatus === 'error' && (
                        <span style={{ 
                            color: '#dc3545', 
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}>
                            ✗ Error submitting feedback. Please try again.
                        </span>
                    )}
                </div>
            </form>

            {existingFeedback && existingFeedback !== feedbackText && (
                <div style={{ 
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px'
                }}>
                    <h4 style={{ 
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        color: '#495057'
                    }}>
                        Previously Submitted Feedback:
                    </h4>
                    <p style={{ 
                        margin: '0',
                        fontSize: '14px',
                        color: '#6c757d',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {existingFeedback}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PilotFeedbackModule; 